import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { CheckCircle2, Download, XCircle } from "lucide-react";

import { lifecycleService } from "../../services/lifecycleService";
import { downloadErrorMessage } from "../../utils/apiError";
import { EVENT_LABELS, day, type Check } from "./lifecycleLogic";

/** Requirements beside what the learner has, each ticked or crossed. */
export function Checks({ checks, title = "Against the criteria" }: { checks: Check[]; title?: string }) {
  if (!checks.length) return null;
  return (
    <section className="space-y-2">
      <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">{title}</h4>
      <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200">
        {checks.map((check) => (
          <li key={check.label} className="flex items-center justify-between gap-3 px-4 py-2 text-sm">
            <span className="flex items-center gap-2 text-gray-700">
              {check.ok ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <XCircle className="h-4 w-4 text-red-600" />}
              {check.label}
            </span>
            <span className="text-right">
              <span className={`font-semibold tabular-nums ${check.ok ? "text-gray-900" : "text-red-700"}`}>{check.value}</span>
              <span className="ml-2 text-xs text-gray-500">{check.need}</span>
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** Why an action isn't offered yet, as a short list. */
export function Blockers({ items }: { items: string[] }) {
  if (!items.length) return null;
  return (
    <div role="note" className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
      <p className="font-medium">It can't be approved yet:</p>
      <ul className="mt-1 list-disc pl-5">{items.map((item) => <li key={item}>{item}</li>)}</ul>
    </div>
  );
}

/** The learner's journey so far: every promotion, move and change of standing. */
export function LearnerHistory({ studentId }: { studentId: number }) {
  const history = useQuery({ queryKey: ["lifecycle", "history", studentId], queryFn: () => lifecycleService.getStudentLifecycleHistory(studentId) });
  const events = history.data?.events ?? [];
  return (
    <section className="space-y-2">
      <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">The learner's record</h4>
      {history.isLoading ? (
        <div className="h-10 animate-pulse rounded bg-gray-100" />
      ) : history.isError ? (
        <p className="text-sm text-red-700">The learner's record could not be loaded.</p>
      ) : events.length ? (
        <ol className="relative space-y-3 border-l border-gray-200 pl-4">
          {events.map((event) => (
            <li key={event.id} className="text-sm">
              <span className="absolute -left-1 mt-1.5 h-2 w-2 rounded-full bg-primary-500" />
              <p className="flex flex-wrap items-baseline justify-between gap-2">
                <span className="font-medium text-gray-900">{EVENT_LABELS[event.event_type] ?? event.event_type}</span>
                <span className="text-xs tabular-nums text-gray-500">{day(event.event_date)}</span>
              </p>
              <p className="whitespace-pre-wrap break-words text-gray-600">{event.description}</p>
              {event.previous_status && event.new_status && <p className="text-xs text-gray-500">{event.previous_status} → {event.new_status}{event.created_by_name ? ` · by ${event.created_by_name}` : ""}</p>}
            </li>
          ))}
        </ol>
      ) : (
        <p className="text-sm text-gray-500">Nothing recorded yet.</p>
      )}
    </section>
  );
}

/** Downloads a certificate, saying why when the server refuses. */
export function CertificateButton({ label, download }: { label: string; download: () => Promise<void> }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  return (
    <span className="inline-flex flex-col items-start gap-1">
      <button
        type="button"
        disabled={busy}
        className="btn btn-outline gap-1.5"
        onClick={async () => {
          setBusy(true);
          setError("");
          try {
            await download();
          } catch (reason) {
            setError(await downloadErrorMessage(reason, "The certificate could not be downloaded."));
          } finally {
            setBusy(false);
          }
        }}
      >
        <Download className="h-4 w-4" /> {busy ? "Preparing…" : label}
      </button>
      {error && <span role="alert" className="text-xs text-red-700">{error}</span>}
    </span>
  );
}
